import * as R         from 'ramda'
import { redirect }   from 'redux-first-router'
import def            from './define-action'
import * as Gtag      from '../gtag'

const INITIAL_STATE = {
    selectedView            : 'poiList',
    poiSearchText           : null,
    venueSearchText         : null,
    showPoiConfirmationModal: false,
    showVenueChangeAlert    : false,
    selectedMenu            : 'venues',
    selectedSubmenu         : null,
    isSubmenuOpen           : false,
    menuItemToBeConfirmed   : null,
    menuItemToBeChanged     : null,
}

// =====================================================================================================================
// Action Constants
// =====================================================================================================================

const ActionConstants = {
    HIDE_POI_CONFIRMATION_MODAL   : 'HIDE_POI_CONFIRMATION_MODAL',
    HIDE_REMOVE_CONFIRMATION_MODAL: 'HIDE_REMOVE_CONFIRMATION_MODAL',
    HIDE_VENUE_CHANGE_ALERT       : 'HIDE_VENUE_CHANGE_ALERT',
    MAP_FEATURE_CLICKED           : 'MAP_FEATURE_CLICKED',
    POI_SEARCH_TEXT_CHANGE        : 'POI_SEARCH_TEXT_CHANGE',
    SET_SELECTED_SUBMENU          : 'SET_SELECTED_SUBMENU',
    SET_SELECTED_MENU             : 'SET_SELECTED_MENU',
    SHOW_POI_CONFIRMATION_MODAL   : 'SHOW_POI_CONFIRMATION_MODAL',
    SHOW_REMOVE_CONFIRMATION_MODAL: 'SHOW_REMOVE_CONFIRMATION_MODAL',
    SHOW_VENUE_CHANGE_ALERT       : 'SHOW_VENUE_CHANGE_ALERT',
    VENUE_SEARCH_TEXT_CHANGE      : 'VENUE_SEARCH_TEXT_CHANGE',
    TOGGLE_SUBMENU                : 'TOGGLE_SUBMENU',
    ASK_TO_CONFIRM_MENU_CHANGE    : 'ASK_TO_CONFIRM_MENU_CHANGE',
    CONFIRM_MENU_CHANGE           : 'CONFIRM_MENU_CHANGE',
}

// =====================================================================================================================
// Actions
// =====================================================================================================================

const Actions = {
    changePoiSearchText         : def(ActionConstants.POI_SEARCH_TEXT_CHANGE, 'venueId', 'searchText'),
    changeVenueSearchText       : def(ActionConstants.VENUE_SEARCH_TEXT_CHANGE, 'searchText'),
    hidePoiConfirmationModal    : def(ActionConstants.HIDE_POI_CONFIRMATION_MODAL, 'userResponse'),
    hideRemoveConfirmationModal : def(ActionConstants.HIDE_REMOVE_CONFIRMATION_MODAL, 'venueId', 'userResponse', 'poiId'),
    hideVenueChangeAlert        : def(ActionConstants.HIDE_VENUE_CHANGE_ALERT),
    mapFeatureClicked           : def(ActionConstants.MAP_FEATURE_CLICKED, 'rawFeatureId'),
    setSelectedSubmenu          : def(ActionConstants.SET_SELECTED_SUBMENU, 'submenuItem'),
    setSelectedMenu             : def(ActionConstants.SET_SELECTED_MENU, 'menuItem'),
    showPoiConfirmationModal    : def(ActionConstants.SHOW_POI_CONFIRMATION_MODAL),
    showRemoveConfirmationModal : def(ActionConstants.SHOW_REMOVE_CONFIRMATION_MODAL, 'venueId'),
    showVenueChangeAlert        : def(ActionConstants.SHOW_VENUE_CHANGE_ALERT),
    toggleSubmenu               : def(ActionConstants.TOGGLE_SUBMENU),
    askToConfirmMenuChange      : def(ActionConstants.ASK_TO_CONFIRM_MENU_CHANGE, 'menuItem'),
    confirmMenuChange           : def(ActionConstants.CONFIRM_MENU_CHANGE, 'wasConfirmed'),
}

// =====================================================================================================================
// Thunks
// =====================================================================================================================

const showConfirmationModal = location => async dispatch => {
    await dispatch(Actions.showPoiConfirmationModal())
    dispatch(redirect(location))
}

const showVenueAlert = location => async dispatch => {
    await dispatch(Actions.showVenueChangeAlert())
    dispatch(redirect(location))
}

const Thunks = {
    showConfirmationModal,
    showVenueAlert,
}

// =====================================================================================================================
// Reducer
// =====================================================================================================================

const reducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
    case ActionConstants.HIDE_POI_CONFIRMATION_MODAL    : return { ...state, showPoiConfirmationModal: false }
    case ActionConstants.HIDE_REMOVE_CONFIRMATION_MODAL : return handleHideRemoveConfirmationModal(state, action)
    case ActionConstants.HIDE_VENUE_CHANGE_ALERT        : return { ...state, showVenueChangeAlert: false }
    case ActionConstants.MAP_FEATURE_CLICKED            : return { ...state, rawFeatureId: action.rawFeatureId }
    case ActionConstants.POI_SEARCH_TEXT_CHANGE         : return handlePoiSearchTextChange(state, action)
    case ActionConstants.SET_SELECTED_SUBMENU           : return { ...state, selectedSubmenu: action.submenuItem }
    case ActionConstants.SET_SELECTED_MENU              : return { ...state, selectedMenu: action.menuItem }
    case ActionConstants.SHOW_POI_CONFIRMATION_MODAL    : return { ...state, showPoiConfirmationModal: true }
    case ActionConstants.SHOW_REMOVE_CONFIRMATION_MODAL : return handleShowRemoveConfirmationModal(state, action)
    case ActionConstants.SHOW_VENUE_CHANGE_ALERT        : return { ...state, showVenueChangeAlert: true }
    case ActionConstants.VENUE_SEARCH_TEXT_CHANGE       : return handleVenueSearchTextChange(state, action)
    case ActionConstants.TOGGLE_SUBMENU                 : return handleToggleSubmenu(state, action)
    case ActionConstants.ASK_TO_CONFIRM_MENU_CHANGE     : return { ...state, menuItemToBeConfirmed: action.menuItem }
    case ActionConstants.CONFIRM_MENU_CHANGE            : return handleConfirmMenuChange(state, action)
    default                                             : return state
    }
}

const handlePoiSearchTextChange = (state, action) => ({ ...state, poiSearchText: action.searchText })

const handleVenueSearchTextChange = (state, action) => ({ ...state, venueSearchText: action.searchText })

const handleHideRemoveConfirmationModal = (state, action) => {
    Gtag.recordHideRemoveConfirmation(action.venueId, action.userResponse, action.poiId)
    return { ...state, showRemoveConfirmationModal: false }
}

const handleShowRemoveConfirmationModal = (state, action) => {
    Gtag.recordShowRemoveConfirmation(action.venueId)
    return { ...state, showRemoveConfirmationModal: true }
}

const handleToggleSubmenu = (state, action) => {
    Gtag.recordMenuClick()
    return { ...state, isSubmenuOpen: !state.isSubmenuOpen }
}

const handleConfirmMenuChange = (state, action) => {
    if(action.wasConfirmed) {
        return { ...state, menuItemToBeChanged: state.menuItemToBeConfirmed, menuItemToBeConfirmed: null }
    }
    return { ...state, menuItemToBeConfirmed: null }
}

// =====================================================================================================================
// Selectors
// =====================================================================================================================

const getPoiSearchText                = state => R.path(['view', 'poiSearchText'], state)
const getRawFeatureId                 = state => R.path(['view', 'rawFeatureId'], state)
const getSelectedSubmenu              = state => R.path(['view', 'selectedSubmenu'], state)
const getSelectedMenu                 = state => R.path(['view', 'selectedMenu'], state)
const getShowPoiConfirmationModal     = state => R.path(['view', 'showPoiConfirmationModal'], state)
const getShowRemoveConfirmationModal  = state => R.path(['view', 'showRemoveConfirmationModal'], state)
const getShowVenueChangeAlert         = state => R.path(['view', 'showVenueChangeAlert'], state)
const getVenueSearchText              = state => R.path(['view', 'venueSearchText'], state)
const getIsSubmenuOpen                = state => R.path(['view', 'isSubmenuOpen'], state)
const getMenuItemToBeConfirmed        = state => R.path(['view', 'menuItemToBeConfirmed'], state)
const getMenuItemToBeChanged          = state => R.path(['view', 'menuItemToBeChanged'], state)

const Selectors = {
    getPoiSearchText,
    getRawFeatureId,
    getSelectedSubmenu,
    getSelectedMenu,
    getShowPoiConfirmationModal,
    getShowRemoveConfirmationModal,
    getShowVenueChangeAlert,
    getVenueSearchText,
    getIsSubmenuOpen,
    getMenuItemToBeConfirmed,
    getMenuItemToBeChanged,
}

export default reducer

export {
    reducer,
    Selectors,
    Actions,
    Thunks,
}

